import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import GameCard from '@/components/base/GameCard';
import { games, gameCategories } from '@/mocks/games';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'role-playing');

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  const currentCategory = gameCategories.find((c) => c.slug === activeCategory);
  const filteredGames = games.filter((g) => g.category === activeCategory);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory?.name || ''} - 黄油网页游戏合集`,
    description: `在线免费畅玩${currentCategory?.name || ''}类黄油网页游戏，精选优质${currentCategory?.name || ''}黄油网页游戏推荐`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '' },
        { '@type': 'ListItem', position: 2, name: '游戏分类', item: typeof window !== 'undefined' ? `${window.location.origin}/category/role-playing` : '' },
        { '@type': 'ListItem', position: 3, name: currentCategory?.name || '' },
      ],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '游戏分类', to: '/category/role-playing' },
            { label: currentCategory?.name || '' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory?.name}黄油网页游戏
            </h1>
            <p className="text-sm text-foreground-500 mb-8">
              精选{currentCategory?.name}类黄油网页游戏，在线免费畅玩，无需下载
            </p>

            <div className="flex items-center gap-2 flex-wrap mb-10">
              {gameCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-600 hover:bg-secondary-100 border border-background-200/70'
                  }`}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </div>
                  {cat.name}
                </Link>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-product-shop="">
              {filteredGames.map((game) => {
                const cat = gameCategories.find((c) => c.slug === game.category);
                return (
                  <GameCard
                    key={game.id}
                    id={game.id}
                    title={game.title}
                    slug={game.slug}
                    category={game.category}
                    categoryName={cat?.name || game.category}
                    image={game.image}
                    rating={game.rating}
                    players={game.players}
                  />
                );
              })}
            </div>

            {filteredGames.length === 0 && (
              <div className="text-center py-16">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-gamepad-line text-4xl text-foreground-300"></i>
                </div>
                <p className="text-foreground-500">该分类暂无黄油网页游戏</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}